import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { customFields as cfApi, deviceCustomFields as dcfApi } from '@/lib/trackizy';

function InputFor({ def, value, onChange, disabled }) {
  const { t } = useTranslation();
  switch (def.field_type) {
    case 'boolean':
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!value} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)} />
          {t('customFields.yes', 'Yes')}
        </label>
      );
    case 'number':
      return (
        <input type="number" value={value ?? ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" />
      );
    case 'date':
      return (
        <input type="date" value={value ?? ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" />
      );
    case 'select':
      return (
        <select value={value ?? ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <option value="">{t('customFields.selectPlaceholder', '— Select —')}</option>
          {(def.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case 'text':
    default:
      return (
        <input type="text" value={value ?? ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" />
      );
  }
}

/**
 * Editable custom fields section for a device.
 * - Loads definitions from /api/custom-fields on mount.
 * - Initial values come from props.initialValues (the device's custom_fields map).
 * - Save is NOT auto-triggered; parent calls the ref's save(deviceId) after the main save.
 */
const CustomFieldsSection = forwardRef(function CustomFieldsSection(
  { initialValues = {}, isNewDevice = false },
  ref
) {
  const { t } = useTranslation();
  const [defs, setDefs] = useState([]);
  const [values, setValues] = useState(initialValues);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    cfApi.list().then((rows) => {
      if (!cancelled) {
        setDefs([...rows].sort((a, b) => a.display_order - b.display_order));
        setLoaded(true);
      }
    }).catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const setValue = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  const isMissingRequired = (def) => {
    const v = values[def.key];
    return def.required && (v === null || v === undefined || v === '');
  };

  useImperativeHandle(ref, () => ({
    /** Call after the device has been saved. Writes values via the Trackizy endpoint. */
    async save(deviceId) {
      if (!loaded) return;
      // Build a diff against initialValues so we only PATCH changed keys
      const patch = {};
      for (const def of defs) {
        const cur = values[def.key];
        const init = initialValues[def.key];
        if (cur === init) continue;
        // Empty string → null (clears the key)
        patch[def.key] = cur === '' || cur === undefined ? null : cur;
      }
      if (Object.keys(patch).length === 0) return;
      await dcfApi.update(deviceId, patch);
    },

    /** Parent calls this in form onSubmit to decide whether to block save for new devices. */
    validateBlockingForNewDevice() {
      if (!isNewDevice) return null;
      const missing = defs.filter(isMissingRequired).map((d) => d.label);
      if (missing.length === 0) return null;
      return t('customFields.missingRequired',
        'Missing required fields: {{fields}}',
        { fields: missing.join(', ') });
    },
  }));

  if (!loaded) return null;
  if (defs.length === 0) return null;

  return (
    <section className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
      <h3 className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
        {t('customFields.sectionTitle', 'Custom Fields')}
      </h3>
      {defs.map((def) => {
        const missing = !isNewDevice && isMissingRequired(def);
        return (
          <label key={def.id} className="block">
            <span className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
              {def.label}
              {def.required && <span className="text-rose-500 ms-1">*</span>}
            </span>
            <InputFor def={def} value={values[def.key]} onChange={(v) => setValue(def.key, v)} />
            {missing && (
              <span className="block text-xs text-amber-600 mt-1">
                {t('customFields.missingOnExisting', 'This field is required for new vehicles.')}
              </span>
            )}
          </label>
        );
      })}
    </section>
  );
});

export default CustomFieldsSection;
